import { c as _c } from "react/compiler-runtime";
import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import gsap from 'gsap';
import { FC, ReactNode, useEffect, useRef } from 'react';
import css from './style.module.scss';
import { distance, lerp } from './utils';
interface MotionTrailProps {
  items: ReactNode[];
  className?: string;
  fadeAfter?: number;
}
const MotionTrail: FC<MotionTrailProps> = t0 => {
  const $ = _c(15);
  const {
    items,
    className,
    fadeAfter: t1
  } = t0;
  const fadeAfter = t1 === undefined ? 1 : t1;
  const containerRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const itemsRef = useRef(t2);
  const isInView = useRef(false);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => itemsRef.current.filter(_temp);
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const getItems = t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const observer = new IntersectionObserver(t6 => {
        const [entry] = t6;
        isInView.current = entry.isIntersecting;
      }, {
        root: null,
        threshold: 0
      });
      if (containerRef.current) {
        observer.observe(containerRef.current);
      }
      return () => {
        observer.disconnect();
      };
    };
    t5 = [];
    $[2] = t4;
    $[3] = t5;
  } else {
    t4 = $[2];
    t5 = $[3];
  }
  useEffect(t4, t5);
  let t6;
  if ($[4] !== fadeAfter) {
    t6 = () => {
      if (!containerRef.current) {
        return;
      }
      let imgPosition = 0;
      let zIndexVal = 1;
      let activeImagesCount = 0;
      let isIdle = true;
      let mousePos = {
        x: 0,
        y: 0
      };
      let lastMousePos = {
        x: 0,
        y: 0
      };
      const cacheMousePos = {
        x: 0,
        y: 0
      };
      const itemElements = getItems();
      const itemsTotal = itemElements.length;
      const handlePointerMove = ev => {
        if (!containerRef.current || !isInView.current) {
          return;
        }
        const rect = containerRef.current.getBoundingClientRect();
        const {
          clientX,
          clientY
        } = ev;
        const isInside = clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
        if (!isInside) {
          return;
        }
        mousePos = {
          x: ev.pageX,
          y: ev.pageY
        };
      };
      window.addEventListener("mousemove", handlePointerMove);
      const showNextItem = () => {
        zIndexVal = zIndexVal + 1;
        zIndexVal;
        imgPosition = imgPosition < itemsTotal - 1 ? imgPosition + 1 : 0;
        const item = itemElements[imgPosition];
        if (!item) {
          return;
        }
        const containerRect = containerRef.current.getBoundingClientRect();
        const docScrollX = window.scrollX || document.documentElement.scrollLeft;
        const docScrollY = window.scrollY || document.documentElement.scrollTop;
        const containerLeft = containerRect.left + docScrollX;
        const containerTop = containerRect.top + docScrollY;
        const relMouseX = mousePos.x - containerLeft;
        const relMouseY = mousePos.y - containerTop;
        const relCacheMouseX = cacheMousePos.x - containerLeft;
        const relCacheMouseY = cacheMousePos.y - containerTop;
        gsap.killTweensOf(item);
        const scaleValue = gsap.utils.random(0.7, 1.3);
        const rect_0 = item.getBoundingClientRect();
        const tl = gsap.timeline({
          onStart: () => {
            activeImagesCount = activeImagesCount + 1;
            activeImagesCount;
            isIdle = false;
          },
          onComplete: () => {
            activeImagesCount = activeImagesCount - 1;
            activeImagesCount;
          }
        });
        const startX = relCacheMouseX - rect_0.width / 2;
        const startY = relCacheMouseY - rect_0.height / 2;
        const endX = relMouseX - rect_0.width / 2;
        const endY = relMouseY - rect_0.height / 2;
        tl.fromTo(item, {
          scale: scaleValue - Math.max(gsap.utils.random(0.2, 0.6), 0),
          rotationZ: 0,
          opacity: 1,
          zIndex: zIndexVal,
          x: startX,
          y: startY
        }, {
          duration: 0.4,
          ease: "power3",
          scale: scaleValue,
          rotationZ: gsap.utils.random(-3, 3),
          x: endX,
          y: endY
        }, 0);
        if (fadeAfter) {
          tl.to(item, {
            opacity: 0,
            duration: 0.2
          }, `>${fadeAfter}`);
        }
      };
      const render = () => {
        if (!isInView.current) {
          requestAnimationFrame(render);
          return;
        }
        const dist = distance(mousePos.x, mousePos.y, lastMousePos.x, lastMousePos.y);
        cacheMousePos.x = lerp(cacheMousePos.x || mousePos.x, mousePos.x, 0.3);
        cacheMousePos.y = lerp(cacheMousePos.y || mousePos.y, mousePos.y, 0.3);
        if (dist > 200) {
          showNextItem();
          lastMousePos = {
            ...mousePos
          };
        }
        if (isIdle && zIndexVal !== 1) {
          zIndexVal = 1;
        }
        requestAnimationFrame(render);
      };
      requestAnimationFrame(render);
      return () => {
        window.removeEventListener("mousemove", handlePointerMove);
      };
    };
    $[4] = fadeAfter;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== items) {
    t7 = {
      scope: containerRef,
      dependencies: [items]
    };
    $[6] = items;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  useGSAP(t6, t7);
  let t8;
  if ($[8] !== className) {
    t8 = clsx(css.trailContainer, className);
    $[8] = className;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== items) {
    t9 = items.map((item_0, i) => <div key={i} ref={el_0 => {
      itemsRef.current[i] = el_0;
    }} className={css.trailItem}>{item_0}</div>);
    $[10] = items;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t8 || $[13] !== t9) {
    t10 = <div ref={containerRef} className={t8}>{t9}</div>;
    $[12] = t8;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  return t10;
};
export default MotionTrail;
function _temp(el) {
  return el !== null;
}